import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import "./testimonial.css";

const testimonials = [
  {
    name: "Client Story 01",
    year: "2026",
    video: require("../../images/testimon1.mp4").default,
    videoType: "video/mp4",
    quote:
      "Great guidance, a cooperative team, quality construction, and quick resolution at every stage.",
  },
  {
    name: "Client Story 02",
    year: "2025",
    video: require("../../images/testimon2.mp4").default,
    videoType: "video/mp4",
    quote:
      "Thoughtful planning and attention to detail made the Arcus home-building experience truly rewarding.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, x: 44 },
  visible: (index) => ({
    opacity: 1,
    x: 0,
    transition: { delay: index * 0.14, duration: 0.55, ease: "easeOut" },
  }),
};

const VideoTestimonialCard = ({ testimonial, index }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const playVideo = () => {
    if (videoRef.current) videoRef.current.play();
  };

  return (
    <motion.article
      className="testimonial-card"
      custom={index}
      variants={cardVariants}
      whileHover={{ y: -7 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className={`testimonial-card__media${isPlaying ? " is-playing" : ""}`}>
        <motion.video
          ref={videoRef}
          controls={isPlaying}
          preload="metadata"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          whileHover={{ scale: 1.025 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <source src={testimonial.video} type={testimonial.videoType} />
        </motion.video>
        {!isPlaying && (
          <button
            className="testimonial-card__play"
            type="button"
            onClick={playVideo}
            aria-label={`Play ${testimonial.name} video`}
          >
            <span aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="testimonial-card__body">
        <div className="testimonial-card__meta">
          <h3>{testimonial.name}</h3>
          <span>{testimonial.year}</span>
        </div>
        <div className="testimonial-card__stars" aria-label="5 out of 5 stars">
          ★★★★★
        </div>
        <p>“{testimonial.quote}”</p>
      </div>
    </motion.article>
  );
};

const TestimonialCards = () => {
  const sectionRef = useRef(null);
  const controls = useAnimation();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !("IntersectionObserver" in window)) {
      controls.start("visible");
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          controls.start("visible");
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [controls]);

  return (
    <motion.section
      ref={sectionRef}
      className="testimonials-section"
      initial="hidden"
      animate={controls}
    >
    <div className="testimonials-inner">
      <motion.p
        className="testimonials-kicker"
        variants={{
          hidden: { opacity: 0, y: -12 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
        }}
      >
        <span className="testimonials-kicker__line" />
        TESTIMONIALS
        <span className="testimonials-kicker__line" />
      </motion.p>

      <div className="testimonials-body">
        <motion.div
          className="testimonials-left"
          variants={{
            hidden: { opacity: 0, x: -44 },
            visible: {
              opacity: 1,
              x: 0,
              transition: { duration: 0.6, ease: "easeOut" },
            },
          }}
        >
          <h2 className="testimonials-title">
            What Our <b>Clients Say</b>
          </h2>
          <p className="testimonials-subtitle">
            Real stories from clients who trusted us and loved the results.
          </p>
        </motion.div>

        <motion.div className="testimonials-cards" variants={{ visible: {} }}>
          {testimonials.map((testimonial, index) => (
            <VideoTestimonialCard
              key={testimonial.name}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </div>
    </motion.section>
  );
};

export default TestimonialCards;
